import React, { useContext, useEffect, useState } from 'react'
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { SET_VIEWER_IMAGES } from '../store';
import LanguageContext from '../helper/LanguageContext';
import { BASE_URL, buildImageUrl } from '../helper/constants';
import Zoom from 'react-medium-image-zoom'
import 'react-medium-image-zoom/dist/styles.css'
import './FullScreenImageViewer.css'
import VideoPlayers from './VideoPlayers';
import VideoPreviewThumb from './chatMedia/VideoPreviewThumb';
import ReactPlayer from 'react-player'
import JSZip from 'jszip'
import { formatDocSize, getDocAccentColor, getDocExtension, getDocIconName, getDocKind, isSpreadsheetDoc } from '../utils/documentAlbum'
import ExcelPreview from './ExcelPreview'
import { canPickDirectory, pickDownloadDirectory } from '../utils/downloadMediaToFolder'
import {
    dismissMediaDownload,
    startMediaDownload,
    useMediaDownload,
} from '../utils/mediaDownloadManager'
import * as moment from 'moment'
import { getLocaleForLanguage } from '../utils/composerUtils'
import { buildVideoPosterUrl, buildVideoSrc, getVideoMimeType, isNativeVideoAttachment } from '../utils/chatVideo'

const VIEWER_DOWNLOAD_SCOPE = 'viewer:lightbox';

function interpolate(template, vars) {
    return Object.keys(vars).reduce(
        (text, key) => text.replace(new RegExp(`\\{${key}\\}`, 'g'), vars[key]),
        template
    );
}

function isNativeViewerVideo(item) {
    if (!item || item.videoType) return false;
    if (item.isNativeVideo || item.fileType === 'video') return true;
    return isNativeVideoAttachment(item);
}

function getNativeViewerSrc(item) {
    if (!item) return '';
    const blobUrl = item.url && (String(item.url).startsWith('blob:') || String(item.url).startsWith('data:')) ? item.url : '';
    const local = !!(item.local || blobUrl);
    return buildVideoSrc(local, item.fileUrl || item.url || item.href, item.attachedFileName || item.fileName || item.name) || blobUrl;
}

function getNativeViewerPoster(item) {
    if (!item) return '';
    const blobUrl = item.url && (String(item.url).startsWith('blob:') || String(item.url).startsWith('data:')) ? item.url : '';
    const local = !!(item.local || blobUrl);
    return buildVideoPosterUrl(local, item.thumbnailUrl, item.fileUrl || item.url || item.href);
}

function isPdfItem(item) {
    if (!item) return false;
    if (item.isPdf) return true;
    if (item.type === 'application/pdf' || item.fileType === 'application/pdf') return true;
    const candidates = [item.fileUrl, item.href, item.url, item.name, item.attachedFileName, item.value, item.fileName];
    for (const c of candidates) {
        if (typeof c === 'string' && /\.pdf(\?|$)/i.test(c)) return true;
    }
    return false;
}

// Documents that the browser can't render inline (anything but a PDF or an
// image) get a typed card instead of a preview. Returns null for everything
// the viewer already knows how to show.
function getNoPreviewDocMeta(item) {
    if (!item || !item.isDoc || item.videoType) return null;
    const kind = item.docKind || getDocKind(item);
    if (kind === 'pdf' || kind === 'image' || kind === 'spreadsheet') return null;
    return {
        name: item.docName || item.fileName || item.attachedFileName || item.name || 'document',
        extension: (item.docExtension || getDocExtension(item) || '').toUpperCase(),
        icon: item.docIcon || getDocIconName(item),
        color: item.docColor || getDocAccentColor(item),
        size: formatDocSize(item.docSize || item.fileSize || 0),
    };
}

function mapViewerThumbs(reduxImages, selectedIndex) {
    return (reduxImages || []).map((e, i) => {
        const docFields = {
            id: e?.id || e?.attachmentId || `viewer-${i}`,
            fileUrl: e?.fileUrl,
            source: e?.source,
            attachmentId: e?.attachmentId,
            mimeType: e?.mimeType,
            isDoc: !!e?.isDoc,
            docKind: e?.docKind,
            docName: e?.docName,
            docExtension: e?.docExtension,
            docIcon: e?.docIcon,
            docColor: e?.docColor,
            docSize: e?.docSize,
        };
        const nativeVideo = !e?.videoType && isNativeViewerVideo(e);
        if (e.size || e?.videoType || nativeVideo) {
            return {
                ...docFields,
                videoType: e?.videoType,
                isNativeVideo: nativeVideo,
                fileType: e?.fileType || (nativeVideo ? 'video' : e?.fileType),
                fileUrl: e?.fileUrl,
                thumbnailUrl: e?.thumbnailUrl,
                attachedFileName: e?.attachedFileName || e?.name,
                url: e.url ? e.url : e.href,
                href: e.href,
                local: !!e.local,
                value: nativeVideo ? (getNativeViewerPoster(e) || getNativeViewerSrc(e)) : getImageUrlFromDataStatic(e),
                selected: i === selectedIndex,
                fileName: e?.docName || e?.attachedFileName || e?.name || '',
                isPdf: isPdfItem(e),
            };
        }
        return {
            ...docFields,
            value: getImageUrlFromDataStatic(e),
            selected: i === selectedIndex,
            fileName: e?.docName || e?.attachedFileName || e?.name || '',
            url: e.url,
            href: e.href,
            local: !!e.local,
            isPdf: isPdfItem(e),
        };
    });
}

function getImageUrlFromDataStatic(item) {
    if (!item) return '';
    if (item.local && item.url) return item.url;
    if (item.url && (String(item.url).startsWith('blob:') || String(item.url).startsWith('http'))) return item.url;
    if (item.value) return item.value;
    const fileUrl = Array.isArray(item.fileUrl) ? item.fileUrl[0] : item.fileUrl;
    if (fileUrl) {
        if (String(fileUrl).startsWith('http') || String(fileUrl).startsWith('blob:')) return fileUrl;
        return BASE_URL + fileUrl;
    }
    if (item.href) return item.href.startsWith('http') ? item.href : BASE_URL + item.href;
    return '';
}

function FullScreenImageViewer({ reduxImages }) {
    const dispatch = useDispatch()
    const { t, language } = useContext(LanguageContext)
    const [images, setImges] = useState([])
    let ind = useSelector(e => e.initalIndex)
    const viewerIsInternal = useSelector(e => e.viewerIsInternal)
    const initalIndex = (ind || 0)
    const [selectedIndex, setSelectedIndex] = useState(initalIndex)
    const selectedIndexRef = React.useRef(initalIndex)
    const [selectedImage, setSelectedImage] = useState('')
    const [selectedFileName, setSelectedFileName] = useState('')
    const [selectedIsPdf, setSelectedIsPdf] = useState(false)
    const [selectedIsSpreadsheet, setSelectedIsSpreadsheet] = useState(false)
    // Set only for documents that can't be previewed inline (everything but PDFs
    // and images), so the main area can fall back to a typed card.
    const [selectedDoc, setSelectedDoc] = useState(null)
    // The file route answers with `Content-Disposition: attachment`, so framing
    // it directly makes the browser download the PDF instead of previewing it.
    // Fetching the bytes and framing a blob URL keeps the preview inline.
    const [pdfObjectUrl, setPdfObjectUrl] = useState('')
    const [pdfLoading, setPdfLoading] = useState(false)
    const [pdfFailed, setPdfFailed] = useState(false)
    const [isDownloadingAll, setIsDownloadingAll] = useState(false)
    const [downloadProgress, setDownloadProgress] = useState(0)
    const [exportMode, setExportMode] = useState(false)
    const [exportIds, setExportIds] = useState(() => new Set())
    const thumbnailsRef = React.useRef(null)
    const downloadJob = useMediaDownload(VIEWER_DOWNLOAD_SCOPE)
    const jobRunning = downloadJob.status === 'running'
    const jobCompleted = downloadJob.status === 'completed' && !!downloadJob.completedAt
    const accent = viewerIsInternal ? 'var(--kjuup-internal)' : 'var(--kjuup-accent)'
    // Helper function to get image URL from various data formats
    const getImageUrlFromData = (item) => {
        if (!item) return '';
        
        // If it's a local file with url property
        if (item.local && item.url) {
            return item.url;
        }
        
        // If url property exists directly (for local/uploaded images)
        if (item.url && !item.videoType) {
            return item.url;
        }
        
        // If href property exists (for embedded URLs from articles)
        if (item.href && !item.videoType) {
            // Check if it's already a complete URL
            if (item.href.startsWith('http://') || item.href.startsWith('https://')) {
                return item.href;
            }
            return BASE_URL + item.href;
        }
        
        // If fileUrl exists
        if (item.fileUrl) {
            const fileUrlValue = Array.isArray(item.fileUrl) ? item.fileUrl[0] : item.fileUrl;
            // Check if it's already a complete URL
            if (fileUrlValue && (fileUrlValue.startsWith('http://') || fileUrlValue.startsWith('https://'))) {
                return fileUrlValue;
            }
            return buildImageUrl(false, item.fileUrl, item.attachedFileName);
        }
        
        return '';
    };

    const showViewerItem = (item) => {
        if (!item) return;
        if (item.videoType) {
            setSelectedImage({ url: item.href || item.url, isVideo: true, videoType: item.videoType });
            setSelectedIsPdf(false);
            setSelectedIsSpreadsheet(false);
            setSelectedDoc(null);
            return;
        }
        if (item.isNativeVideo || isNativeViewerVideo(item)) {
            setSelectedImage({
                url: getNativeViewerSrc(item),
                isVideo: true,
                isNativeVideo: true,
                local: !!(item.local || (item.url && String(item.url).startsWith('blob:'))),
                fileName: item.fileName || item.attachedFileName || item.name || '',
                thumbnailUrl: item.thumbnailUrl,
                poster: getNativeViewerPoster(item),
            });
            setSelectedFileName(item.fileName || item.attachedFileName || item.name || '');
            setSelectedIsPdf(false);
            setSelectedIsSpreadsheet(false);
            setSelectedDoc(null);
            return;
        }
        const imgUrl = item.value || getImageUrlFromData(item);
        setSelectedImage(imgUrl);
        setSelectedFileName(item.docName || item.fileName || item.attachedFileName || item.name || '');
        setSelectedIsPdf(!!item.isPdf || isPdfItem({ ...item, url: imgUrl }));
        setSelectedIsSpreadsheet(isSpreadsheetDoc(item));
        setSelectedDoc(getNoPreviewDocMeta(item));
    };

    const galleryKeyRef = React.useRef('');

    useEffect(() => {
        selectedIndexRef.current = initalIndex || 0;
        setSelectedIndex(initalIndex || 0);
        if (reduxImages && reduxImages[initalIndex || 0]) {
            showViewerItem(reduxImages[initalIndex || 0]);
            setImges(mapViewerThumbs(reduxImages, initalIndex || 0));
        }
    }, [initalIndex]);

    useEffect(() => {
        if (!reduxImages || reduxImages.length === 0) {
            galleryKeyRef.current = '';
            setImges([]);
            return;
        }
        const first = reduxImages[0];
        const key = `${reduxImages.length}:${first?.id || first?.fileUrl || first?.url || first?.href || ''}`;
        const isNewGallery = key !== galleryKeyRef.current;
        galleryKeyRef.current = key;
        const idx = isNewGallery
            ? (initalIndex || 0)
            : Math.min(selectedIndexRef.current, reduxImages.length - 1);
        if (isNewGallery) {
            selectedIndexRef.current = idx;
            setSelectedIndex(idx);
            showViewerItem(reduxImages[idx]);
        }
        setImges(mapViewerThumbs(reduxImages, idx));
    }, [reduxImages]);

    const selectedPdfUrl = selectedIsPdf
        ? (typeof selectedImage === 'string' ? selectedImage : (selectedImage?.url || ''))
        : ''

    useEffect(() => {
        setPdfObjectUrl('')
        setPdfFailed(false)
        if (!selectedPdfUrl) {
            setPdfLoading(false)
            return undefined
        }
        let cancelled = false
        let objectUrl = ''
        setPdfLoading(true)
        fetch(selectedPdfUrl)
            .then((response) => response.arrayBuffer())
            .then((buffer) => {
                if (cancelled) return
                objectUrl = URL.createObjectURL(new Blob([buffer], { type: 'application/pdf' }))
                setPdfObjectUrl(objectUrl)
            })
            .catch((error) => {
                console.error('Error loading PDF preview:', error)
                if (!cancelled) setPdfFailed(true)
            })
            .finally(() => {
                if (!cancelled) setPdfLoading(false)
            })
        return () => {
            cancelled = true
            if (objectUrl) URL.revokeObjectURL(objectUrl)
        }
    }, [selectedPdfUrl])

    // Keep the currently selected thumbnail scrolled into view so every image
    // stays reachable even when the strip overflows horizontally.
    useEffect(() => {
        const container = thumbnailsRef.current;
        if (!container) return;
        const activeThumb = container.querySelector('.photo-preview-thumb.active');
        if (activeThumb && typeof activeThumb.scrollIntoView === 'function') {
            activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
    }, [images])

    useEffect(() => {
        if (downloadJob.status === 'running' && downloadJob.itemIds?.length) {
            setExportIds(new Set(downloadJob.itemIds));
        }
        if (downloadJob.status !== 'completed') return;
        setExportMode(false);
        setExportIds(new Set());
    }, [downloadJob.status]);

    const _selectImage = (item, i) => {
        selectedIndexRef.current = i;
        setSelectedIndex(i);
        showViewerItem(item);
        setImges((prev) => prev.map((x, y) => ({ ...x, selected: y === i })));
    }

    const _renderVideoPlayerPlaceholder = (e) => {
        const ytId = String(e.url || e.href || '').match(
            /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/))([A-Za-z0-9_-]{11})/
        )?.[1];
        const thumbSrc = ytId ? `https://img.youtube.com/vi/${ytId}/mqdefault.jpg` : '';
        return (
            <div className="full-screen-image-viewer__video-thumb">
                {thumbSrc ? (
                    <img src={thumbSrc} alt="" />
                ) : (
                    <ReactPlayer
                        light={true}
                        url={e.url || e.href}
                        controls={false}
                        muted={true}
                        height={'100%'}
                        width={'100%'}
                        playIcon={<></>}
                        style={{ borderRadius: 0, overflow: 'hidden', background: '#111' }}
                    />
                )}
                <div className="full-screen-image-viewer__video-thumb-overlay">
                    <div className="kj-flex-1" />
                    <img className="full-screen-image-viewer__play-icon" src={process.env.PUBLIC_URL + '/assets/img/DN4X.png'} alt="" />
                    <div className="kj-flex-1" />
                </div>
            </div>
        );
    };
    const isDesktopResolution = useSelector(state => state.isDesktopResolution)

    // Resolve a downloadable URL for a thumbnail data item (images / pdfs only).
    const getDownloadUrlFromThumb = (item) => {
        if (!item || item.videoType) return '';
        if (isNativeViewerVideo(item)) return getNativeViewerSrc(item);
        return item.value || item.url || (item.href ? (item.href.startsWith('http') ? item.href : BASE_URL + item.href) : '');
    };

    // Fetch a single file and trigger a browser download.
    const downloadFile = async (fileUrl, fileName, isPdf) => {
        if (!fileUrl) return;
        const response = await fetch(fileUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        let name = fileName;
        if (!name) {
            const urlParts = fileUrl.split('/');
            name = urlParts[urlParts.length - 1].split('?')[0] || (isPdf ? 'document.pdf' : 'image.jpg');
        }
        link.download = name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const handleDownload = async () => {
        if (!selectedImage || selectedImage?.videoType) return;
        try {
            const imageUrl = typeof selectedImage === 'string' ? selectedImage : selectedImage.url;
            await downloadFile(imageUrl, selectedFileName, selectedIsPdf);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    // Downloadable items: everything that isn't a video.
    const downloadableImages = images.filter((e) => !e?.videoType);
    const allExportSelected =
        downloadableImages.length > 0 &&
        downloadableImages.every((item) => exportIds.has(item.id));

    const getThumbId = (item, index) => item?.id || `viewer-${index}`;

    const toDownloadItem = (item, index) => {
        const url = getDownloadUrlFromThumb(item);
        return {
            id: getThumbId(item, index),
            fileUrl: item.fileUrl || url,
            attachedFileName: item.fileName || item.attachedFileName,
            messageType: item.videoType || item.isNativeVideo
                ? 'video'
                : item.isPdf || (item.isDoc && item.docKind !== 'image')
                    ? 'doc'
                    : 'image',
            source: item.source,
            attachmentId: item.attachmentId,
            mimeType: item.mimeType,
        };
    };

    const toggleExportId = (id) => {
        if (jobRunning) return;
        setExportIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleExportMode = () => {
        if (jobRunning) return;
        dismissMediaDownload(VIEWER_DOWNLOAD_SCOPE);
        setExportMode((prev) => !prev);
        setExportIds(new Set());
    };

    const toggleSelectAllExport = () => {
        if (jobRunning) return;
        const allIds = downloadableImages.map((item) => item.id).filter(Boolean);
        setExportIds(allExportSelected ? new Set() : new Set(allIds));
    };

    const handleExportSelection = async () => {
        if (jobRunning || exportIds.size === 0) return;
        const items = images
            .map((item, index) => ({ item, index }))
            .filter(({ item, index }) => !item.videoType && exportIds.has(getThumbId(item, index)))
            .map(({ item, index }) => toDownloadItem(item, index));
        if (items.length === 0) return;
        const picked = await pickDownloadDirectory();
        if (picked.cancelled) return;
        const started = startMediaDownload({
            scopeId: VIEWER_DOWNLOAD_SCOPE,
            items,
            dirHandle: picked.dirHandle,
            kind: items.every((row) => row.messageType === 'doc') ? 'docs' : 'media',
        });
        if (started.started) {
            setExportMode(false);
        }
    };

    // Derive a safe, unique file name for an entry inside the zip.
    const buildZipEntryName = (item, fileUrl, index, usedNames) => {
        let name = item?.fileName || '';
        if (!name) {
            const urlParts = fileUrl.split('/');
            name = (urlParts[urlParts.length - 1] || '').split('?')[0];
        }
        if (!name) {
            name = item?.isPdf ? `document-${index + 1}.pdf` : `image-${index + 1}.jpg`;
        }
        // Ensure the file name has an extension.
        if (!/\.[a-z0-9]+$/i.test(name)) {
            name += item?.isPdf ? '.pdf' : '.jpg';
        }
        // De-duplicate names so nothing gets overwritten in the archive.
        let candidate = name;
        let counter = 1;
        while (usedNames.has(candidate)) {
            const dot = name.lastIndexOf('.');
            const base = dot === -1 ? name : name.slice(0, dot);
            const ext = dot === -1 ? '' : name.slice(dot);
            candidate = `${base} (${counter})${ext}`;
            counter++;
        }
        usedNames.add(candidate);
        return candidate;
    };

    const handleDownloadAll = async () => {
        if (isDownloadingAll || downloadableImages.length === 0) return;
        setIsDownloadingAll(true);
        setDownloadProgress(0);
        try {
            const zip = new JSZip();
            const usedNames = new Set();
            const total = downloadableImages.length;
            let added = 0;
            // Phase 1 (0-80%): fetch each file and add it to the archive.
            for (let i = 0; i < total; i++) {
                const item = downloadableImages[i];
                const fileUrl = getDownloadUrlFromThumb(item);
                if (fileUrl) {
                    try {
                        const response = await fetch(fileUrl);
                        const blob = await response.blob();
                        const entryName = buildZipEntryName(item, fileUrl, i, usedNames);
                        zip.file(entryName, blob);
                        added++;
                    } catch (err) {
                        console.error('Error adding file to zip:', err);
                    }
                }
                setDownloadProgress(Math.round(((i + 1) / total) * 80));
            }
            if (added === 0) return;
            // Phase 2 (80-100%): compress the archive.
            const zipBlob = await zip.generateAsync({ type: 'blob' }, (metadata) => {
                setDownloadProgress(80 + Math.round((metadata.percent || 0) * 0.2));
            });
            setDownloadProgress(100);
            const url = window.URL.createObjectURL(zipBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `images-${Date.now()}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error creating zip:', error);
        } finally {
            setIsDownloadingAll(false);
            setDownloadProgress(0);
        }
    };

    return (
        <div 
            className={`photo-preview-overlay light full-screen-image-viewer ${viewerIsInternal ? 'photo-preview-internal' : ''}`}
            onClick={() => dispatch({ type: SET_VIEWER_IMAGES, ViewerImages: [] })}
        >
            <div className="photo-preview-container full-screen-image-viewer__container" onClick={(e) => e.stopPropagation()}>
                {/* Zip download progress */}
                {isDownloadingAll && (
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="full-screen-image-viewer__zip-overlay"
                    >
                        <div className="full-screen-image-viewer__zip-card">
                            {/* Label sits on top of the progress bar, localized to the UI language */}
                            <div className="full-screen-image-viewer__zip-label">
                                {downloadProgress >= 80 ? t('compressing') : t('preparing_zip')}
                            </div>
                            <div className="full-screen-image-viewer__zip-track">
                                <div
                                    className="full-screen-image-viewer__zip-fill"
                                    style={{ '--progress': `${downloadProgress}%` }}
                                />
                            </div>
                            <div className="full-screen-image-viewer__zip-percent">
                                {downloadProgress}%
                            </div>
                        </div>
                    </div>
                )}
                {/* Header */}
                <div className="photo-preview-header">
                    <div className="kj-flex-1" />
                    <div className="full-screen-image-viewer__header-actions">
                        {!selectedImage?.videoType && !exportMode && !jobRunning && (
                            <button 
                                onClick={handleDownload}
                                className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral"
                                title="Download"
                            >
                                <span 
                                    className="material-symbols-outlined full-screen-image-viewer__header-icon"
                                >
                                    download
                                </span>
                            </button>
                        )}
                        {downloadableImages.length > 1 && !exportMode && !jobRunning && (
                            <button 
                                onClick={handleDownloadAll}
                                disabled={isDownloadingAll}
                                className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral"
                                title={isDownloadingAll ? 'Preparing zip…' : `Download all as zip (${downloadableImages.length})`}
                            >
                                <span 
                                    className="material-symbols-outlined full-screen-image-viewer__header-icon"
                                >
                                    download_for_offline
                                </span>
                            </button>
                        )}
                        {downloadableImages.length > 0 && !jobRunning && (
                            <button
                                onClick={toggleExportMode}
                                className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral"
                                title={exportMode ? t('cancel') : (t('select_files') || 'Select files')}
                            >
                                <span
                                    className={`material-symbols-outlined full-screen-image-viewer__header-icon${exportMode ? ' full-screen-image-viewer__header-icon--active' : ''}`}
                                >
                                    {exportMode ? 'cancel' : 'library_add_check'}
                                </span>
                            </button>
                        )}
                        <button 
                            onClick={() => dispatch({ type: SET_VIEWER_IMAGES, ViewerImages: [] })}
                            className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral"
                        >
                            <span 
                                className="material-symbols-outlined full-screen-image-viewer__header-icon"
                            >
                                close
                            </span>
                        </button>
                    </div>
                </div>
                {(exportMode || jobRunning || jobCompleted) && (
                    <div className="photo-preview-export-bar" onClick={(e) => e.stopPropagation()}>
                        {jobRunning && (
                            <div className="photo-preview-export-progress">
                                <div className="photo-preview-export-progress-copy">
                                    <span>
                                        {interpolate(
                                            t('media_download_progress') || 'Downloading {current} of {total}',
                                            {
                                                current: String(Math.min((downloadJob.completedCount || 0) + 1, downloadJob.totalCount || 0)),
                                                total: String(downloadJob.totalCount || 0),
                                            }
                                        )}
                                    </span>
                                    <span>{downloadJob.percent || 0}%</span>
                                </div>
                                <div className="photo-preview-export-track">
                                    <div
                                        className="photo-preview-export-fill full-screen-image-viewer__export-fill"
                                        style={{ '--progress': `${downloadJob.percent || 0}%` }}
                                    />
                                </div>
                            </div>
                        )}
                        {jobCompleted && (
                            <div className="photo-preview-export-note">
                                <span
                                    className="material-symbols-outlined full-screen-image-viewer__note-icon"
                                >
                                    check_circle
                                </span>
                                <span>
                                    {interpolate(
                                        downloadJob.kind === 'docs'
                                            ? (t('docs_downloaded_on') || 'Documents downloaded on {datetime}')
                                            : (t('media_downloaded_on') || 'Images downloaded on {datetime}'),
                                        {
                                            datetime: moment(downloadJob.completedAt)
                                                .locale(getLocaleForLanguage(language))
                                                .format('L, LT'),
                                        }
                                    )}
                                </span>
                                <button
                                    type="button"
                                    className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral"
                                    onClick={() => dismissMediaDownload(VIEWER_DOWNLOAD_SCOPE)}
                                    title={t('cancel') || 'Close'}
                                >
                                    <span className="material-symbols-outlined full-screen-image-viewer__icon-18">close</span>
                                </button>
                            </div>
                        )}
                        {exportMode && !jobRunning && (
                            <div className="photo-preview-export-actions">
                                <button
                                    type="button"
                                    className="kj-btn kj-btn--outline kj-btn--on-surface"
                                    onClick={toggleSelectAllExport}
                                >
                                    <span className="material-symbols-outlined full-screen-image-viewer__icon-20">
                                        {allExportSelected ? 'deselect' : 'select_all'}
                                    </span>
                                    {allExportSelected
                                        ? (t('deselect_all') || 'Deselect All')
                                        : (t('select_all') || 'Select All')}
                                </button>
                                {exportIds.size > 0 && (
                                    <button
                                        type="button"
                                        className="kj-btn kj-btn--outline kj-btn--on-surface"
                                        onClick={handleExportSelection}
                                    >
                                        <span className="material-symbols-outlined full-screen-image-viewer__icon-20">
                                            {canPickDirectory() ? 'folder' : 'folder_zip'}
                                        </span>
                                        {`${canPickDirectory()
                                            ? (t('download_to_folder') || 'Download to folder')
                                            : (t('download_as_zip') || 'Download as ZIP')} (${exportIds.size})`}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}
                
                {/* Main content */}
                <div className="photo-preview-main">
                    {selectedIsPdf ?
                        <div className="photo-preview-image-container full-screen-image-viewer__fill-center">
                            {pdfLoading || pdfFailed ? (
                                <div className="doc-preview-loading">
                                    {pdfFailed ? t('no_preview_available') : t('loading')}
                                </div>
                            ) : (
                                <iframe
                                    title={selectedFileName || 'PDF document'}
                                    src={pdfObjectUrl}
                                    className={`full-screen-image-viewer__pdf-frame${isDesktopResolution ? ' full-screen-image-viewer__pdf-frame--desktop' : ''}`}
                                />
                            )}
                        </div>
                        :
                    selectedIsSpreadsheet ?
                        <div className="photo-preview-image-container full-screen-image-viewer__fill-center">
                            <ExcelPreview
                                src={typeof selectedImage === 'string' ? selectedImage : (selectedImage?.url || '')}
                                fileName={selectedFileName}
                                accentColor={viewerIsInternal ? 'var(--kjuup-internal)' : '#1d6f42'}
                                loadingLabel={t('loading')}
                                failedLabel={t('no_preview_available')}
                                truncatedLabel={t('spreadsheet_preview_truncated')}
                                onDownload={handleDownload}
                                downloadLabel={t('open_file')}
                            />
                        </div>
                        :
                    selectedDoc ?
                        <div className="photo-preview-image-container">
                            <div className="doc-no-preview-card">
                                <div
                                    className="doc-no-preview-icon full-screen-image-viewer__doc-icon-box"
                                    style={{ '--doc-bg': `${selectedDoc.color}1a`, '--doc-color': selectedDoc.color }}
                                >
                                    <span
                                        className="material-symbols-outlined full-screen-image-viewer__doc-icon"
                                    >
                                        {selectedDoc.icon}
                                    </span>
                                    {selectedDoc.extension ? (
                                        <span
                                            className="doc-no-preview-badge full-screen-image-viewer__doc-badge"
                                        >
                                            {selectedDoc.extension.slice(0, 5)}
                                        </span>
                                    ) : null}
                                </div>
                                <div className="doc-no-preview-name" title={selectedDoc.name}>
                                    {selectedDoc.name}
                                </div>
                                {selectedDoc.size ? (
                                    <div className="doc-no-preview-size">{selectedDoc.size}</div>
                                ) : null}
                                <div className="doc-no-preview-title">{t('no_preview_available')}</div>
                                <div className="doc-no-preview-hint">{t('no_preview_hint')}</div>
                                <button
                                    type="button"
                                    className="kj-btn kj-btn--primary kj-btn--pill doc-no-preview-btn"
                                    onClick={handleDownload}
                                >
                                    <span className="material-symbols-outlined">
                                        download
                                    </span>
                                    {t('open_file')}
                                </button>
                            </div>
                        </div>
                        :
                    !selectedImage?.videoType && !selectedImage?.isNativeVideo ?
                        <div className="photo-preview-image-container">
                            <Zoom>
                                <img
                                    src={selectedImage}
                                    className="photo-preview-image full-screen-image-viewer__image"
                                />
                            </Zoom>
                        </div>
                        :
                        selectedImage?.isNativeVideo ?
                        <div className="photo-preview-image-container">
                            <video
                                controls
                                playsInline
                                preload="metadata"
                                src={selectedImage.url}
                                poster={selectedImage.poster || undefined}
                                className="full-screen-image-viewer__video"
                            >
                                <source src={selectedImage.url} type={getVideoMimeType(selectedImage.fileName || selectedImage.url)} />
                            </video>
                        </div>
                        :
                        <div className="photo-preview-image-container photo-preview-link-video">
                            <VideoPlayers
                                url={selectedImage.url}
                                type={selectedImage?.videoType}
                                isPlaying={''}
                                fileType={''}
                                local={selectedImage?.local}
                                article={{}}
                                isC={false}
                                index={0}
                                removeImage={(e) => { }}
                                muted={false}
                                fill
                            />
                        </div>
                    }
                </div>
                
                {/* Thumbnails */}
                <div className="photo-preview-thumbnails" ref={thumbnailsRef}>
                    {images.map((e, i) => {
                        const thumbId = getThumbId(e, i);
                        const isExportChecked = exportIds.has(thumbId);
                        const isJobTarget = jobRunning && downloadJob.itemIds.includes(thumbId);
                        const isJobCurrent = jobRunning && downloadJob.currentId === thumbId;
                        const isJobDone = jobRunning && downloadJob.completedIds.includes(thumbId);
                        const isDimmed = (exportMode || jobRunning) && !e.videoType && !isExportChecked && !isJobTarget;
                        return (
                            <div 
                                key={thumbId}
                                onPointerDown={(ev) => {
                                    if (ev.pointerType === 'mouse' && ev.button !== 0) return;
                                    _selectImage(e, i);
                                }}
                                onClick={() => {
                                    if (exportMode && !e.videoType && !jobRunning) {
                                        toggleExportId(thumbId);
                                    }
                                }}
                                className={`photo-preview-thumb ${e.selected ? 'active' : ''}${isExportChecked || isJobTarget ? ' export-checked' : ''}${isDimmed ? ' export-dimmed' : ''}`}
                            >
                                {e?.videoType ? _renderVideoPlayerPlaceholder(e) :
                                    e?.isNativeVideo ? (
                                        <VideoPreviewThumb
                                            item={{
                                                url: e.url,
                                                fileUrl: e.fileUrl || e.url,
                                                thumbnailUrl: e.thumbnailUrl,
                                                local: e.local,
                                                attachedFileName: e.attachedFileName || e.fileName,
                                            }}
                                            playSize={20}
                                        />
                                    ) :
                                    e?.isPdf ? (
                                        <div
                                            className="full-screen-image-viewer__pdf-thumb"
                                            title={e.fileName || 'PDF document'}
                                        >
                                            <span
                                                className="material-symbols-outlined full-screen-image-viewer__pdf-thumb-icon"
                                            >
                                                picture_as_pdf
                                            </span>
                                            <span className="full-screen-image-viewer__thumb-label">PDF</span>
                                        </div>
                                    ) :
                                    (e?.isDoc && e?.docKind !== 'image') ? (
                                        <div
                                            className="photo-preview-doc-thumb full-screen-image-viewer__doc-thumb"
                                            style={{ '--doc-bg': `${e.docColor || '#5f6368'}14`, '--doc-color': e.docColor || '#5f6368' }}
                                            title={e.fileName || 'Document'}
                                        >
                                            <span
                                                className="material-symbols-outlined full-screen-image-viewer__doc-thumb-icon"
                                            >
                                                {e.docIcon || 'draft'}
                                            </span>
                                            <span className="full-screen-image-viewer__thumb-label">
                                                {(e.docExtension || '').toUpperCase().slice(0, 4)}
                                            </span>
                                        </div>
                                    ) :
                                    <img
                                        src={e.value || e.url || (e.href ? (e.href.startsWith('http') ? e.href : BASE_URL + e.href) : '')}
                                        alt=""
                                    />
                                }
                                {exportMode && !e.videoType && !jobRunning && (
                                    <label className="photo-preview-thumb-check">
                                        <input
                                            type="checkbox"
                                            checked={isExportChecked}
                                            readOnly
                                            tabIndex={-1}
                                        />
                                    </label>
                                )}
                                {isJobCurrent && (
                                    <div className="photo-preview-thumb-progress">{downloadJob.currentProgress || 0}%</div>
                                )}
                                {isJobDone && (
                                    <span
                                        className="material-symbols-outlined photo-preview-thumb-done full-screen-image-viewer__done"
                                    >
                                        check_circle
                                    </span>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

export default FullScreenImageViewer
